import { Box, Divider } from '@mui/material';
import { AutomationsDeployButton } from '../common/deploy-button/AutomationDeployButton';
import useCheckPermissionsForCurrentCrmOrg from '../common/permissions/useCurrentCrmOrgPermissions';
import { useAutomationsContext } from './AutomationsContext';
import { useCallback, useState } from 'react';
import { useValidateAllAndReturnIsValid } from '../common/sweep-condition-rule-builder/MultipleRuleBuildersContext';
import { PartialAutomationStructure } from './AutomationsForm';
import { clearFirstConditionIfEmpty } from '../common/rule-builder/helpers';
import { AutomationType } from '../../types/enums/AutomationType';
import { AutomationUserSettings } from './AutomationUserSettings';
import { AutomationPermissionModeSelector } from './AutomationPermissionModeSelector';
import { StyledDialogActions } from '../common/StyledDialogActions';
import { Button } from '@sweep-io/sweep-design';
import { useFeatureToggle } from '../../feature-flags/useFeatureToggle';
import { useSelector } from 'react-redux';
import { selectUserInfoData } from '../../reducers/userInfoReducer';
import { useAutomationsFormValidationCtx } from './automation-form-validation/AutomationsFormValidationCtx';
import { AutomationDeployAs } from '../../types/enums/AutomationDeployAs';
import { SaveOrCreateAutomationParams } from './useAutomationApiPerType';
interface AutomationDialogActionsProps {
  crmOrgId: string;
  readonly?: boolean;
  onSaveOrCreate: (
    params: SaveOrCreateAutomationParams,
  ) => Promise<{ versionId?: string; automationId?: string; isError?: string }>;
  toggleImpactAnalysis?: () => void;
  isImpactAnalysisOpen: boolean;
}

export const AutomationDialogActions = ({
  onSaveOrCreate,
  crmOrgId,
  readonly = false,
  toggleImpactAnalysis,
  isImpactAnalysisOpen,
}: AutomationDialogActionsProps) => {
  const dataByVariant = useAutomationsContext();
  const { automationJson, automationType, permissions, isObjectless, impactAnalysisAiSupport } =
    dataByVariant;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDedupeMatching =
    automationType === AutomationType.Dedupe || automationType === AutomationType.Matching;

  const { impactAnalysisAiAgent } = useFeatureToggle();
  const user = useSelector(selectUserInfoData);
  const disableAi = !!user?.account?.disableAi;
  const showImpactAnalysisButton =
    impactAnalysisAiAgent && impactAnalysisAiSupport && !disableAi && !!toggleImpactAnalysis;

  const [isAllowedBtn, isAllowedDeployBtn] = useCheckPermissionsForCurrentCrmOrg([
    permissions.edit,
    permissions.deploy,
  ]);
  if (!isAllowedBtn) {
    readonly = true;
  }
  readonly = isSubmitting || readonly;

  const validateAllRuleBuilders = useValidateAllAndReturnIsValid();

  const _createOrSaveAutomation = useCallback(
    async (crmOrgIds: string[], deployAs?: AutomationDeployAs) => {
      const isValid = validateAllRuleBuilders();
      if (!isValid) {
        return { isError: 'Invalid rule builder' };
      }
      setIsSubmitting(true);
      const savedAutomation = await onSaveOrCreate({
        automation: _cleanConditionTriggerBeforeSaving(automationJson) as AutomationStructureNew,
        crmOrgIds,
        deployAs,
      });
      setIsSubmitting(false);
      return savedAutomation;
    },
    [automationJson, onSaveOrCreate, validateAllRuleBuilders],
  );

  const _cleanConditionTriggerBeforeSaving = (_automationJson: PartialAutomationStructure) => {
    if (_automationJson.automationDetails?.when) {
      const tempEc = _automationJson?.automationDetails?.when?.criteria;
      if (tempEc) {
        const tempNewEc = clearFirstConditionIfEmpty(tempEc);
        if (tempNewEc.criteria.length === 0) {
          return {
            ..._automationJson,
            automationDetails: {
              ..._automationJson.automationDetails,
              when: undefined,
            },
          };
        } else {
          const temp: CriteriaDetails = {
            ..._automationJson.automationDetails?.when,
            criteria: tempNewEc,
          };
          return {
            ..._automationJson,
            automationDetails: {
              ..._automationJson.automationDetails,
              when: temp,
            },
          };
        }
      } else {
        return {
          ..._automationJson,
          automationDetails: {
            ..._automationJson.automationDetails,
            when: undefined,
          },
        };
      }
    }
    return _automationJson;
  };

  const isShowOrder =
    automationJson?.type === AutomationType.Default ||
    automationJson?.type === AutomationType.Alert ||
    automationJson?.type === AutomationType.Assignment;

  const isHidePermissionModeSelector =
    automationJson?.type === AutomationType.MarketingAttributionModel;

  const userPermissionContent = isShowOrder ? (
    <AutomationUserSettings crmOrgId={crmOrgId} readonly={readonly} />
  ) : isHidePermissionModeSelector ? null : (
    <AutomationPermissionModeSelector />
  );

  const { isDeployToSfValid, defaultDeployType, isDeployToFlowValid } =
    useAutomationsFormValidationCtx();

  const isAutomationsFormMainActionValid =
    defaultDeployType === AutomationDeployAs.FLOW ? isDeployToFlowValid : isDeployToSfValid;

  return (
    <StyledDialogActions
      sx={{ justifyContent: showImpactAnalysisButton ? 'space-between' : 'flex-end' }}
    >
      {showImpactAnalysisButton && (
        <Button
          pressed={isImpactAnalysisOpen}
          variant="system"
          size="medium"
          onClick={toggleImpactAnalysis}
          disabled={readonly || !isAutomationsFormMainActionValid}
        >
          Impact analysis AI
        </Button>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'center',
          gap: 2,
          alignSelf: 'flex-end',
        }}
      >
        <AutomationsDeployButton
          onDeployOrSave={_createOrSaveAutomation}
          disabled={
            !isAutomationsFormMainActionValid ||
            ((!isAllowedBtn || readonly) && !isAllowedDeployBtn)
          }
        />
        {!isDedupeMatching && !isObjectless && userPermissionContent && (
          <>
            <Divider orientation="vertical" variant="fullWidth" flexItem />
            {userPermissionContent}
          </>
        )}
      </Box>
    </StyledDialogActions>
  );
};
